import { AnyAction } from "redux";

export interface DraftStep {
  step: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed";
  messages?: string[];
}

export interface MatterDraftJob {
  draftId: string;
  correlationId: string;
  isConnecting: boolean;
  isDraftGenerating: boolean;
  status: "Queued" | "In Progress" | "Completed" | "Failed" | "Cancelled";
  progress: DraftStep[];
  serviceIcon?: string;
  drafts: { [key: string]: any };
  content?: string;
  version?: number;
  finalStatus?: string;
  citations?: string[];
  justifications?: { [key: string]: string };
}

interface MatterDraftState {
  jobs: {
    [matterId: string]: {
      [draftId: number]: MatterDraftJob;
    };
  };
}

const blankMatterDraftJob = (draftId: string, correlationId: string): MatterDraftJob => ({
  draftId,
  correlationId,
  isConnecting: false,
  isDraftGenerating: false,
  status: "Queued",
  progress: [],
  drafts: [],
  citations: [],
  justifications: {},
});

const mergeStep = (progress: DraftStep[], patch: DraftStep): DraftStep[] => {
  const idx = progress.findIndex((s) => s.step === patch.step);
  if (idx >= 0) {
    const existing = progress[idx];
    const updated = {
      ...existing,
      status: patch.status,
      messages: [...(existing.messages || []), ...(patch.messages || [])],
    };
    return [...progress.slice(0, idx), updated, ...progress.slice(idx + 1)];
  }
  return [...progress, patch];
};

const initialState: MatterDraftState = {
  jobs: {},
};

export const matterDraftReducer = (state = initialState, action: AnyAction): MatterDraftState => {
  const matterId = action.payload?.matterId;
  const draftId = action.payload?.draftId;
  const correlationId = action.payload?.correlationId;

  if (!matterId || !draftId) return state;

  const existingJob: MatterDraftJob =
    state.jobs?.[matterId]?.[draftId] ?? blankMatterDraftJob(draftId.toString(), correlationId || "");

  const baseJob: MatterDraftJob = correlationId
    ? { ...existingJob, correlationId }
    : existingJob;

  switch (action.type) {
    case "MATTER_DRAFT_JOB_CONNECTING":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: { ...baseJob, isConnecting: true },
          },
        },
      };

    case "MATTER_DRAFT_JOB_STARTED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              isConnecting: false,
              isDraftGenerating: true,
              status: "In Progress",
              progress: action.payload.progress || [],
              serviceIcon: action.payload.service_icon,
            },
          },
        },
      };

    case "MATTER_DRAFT_PROGRESS_UPDATE": {
      const newProgress = Array.isArray(action.payload.progress)
        ? action.payload.progress
        : mergeStep(baseJob.progress, action.payload);

      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: true,
              progress: newProgress,
              serviceIcon: action.payload.service_icon || baseJob.serviceIcon,
            },
          },
        },
      };
    }

    case "MATTER_DRAFT_GENERATION_COMPLETE": {
      const { final } = action.payload;
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Completed",
              content: final?.generated_data?.html_draft,
              version: final?.version,
              citations: final?.citations,
              justifications: final?.justifications,
              progress: action.payload.progress || baseJob.progress,
            },
          },
        },
      };
    }

    case "MATTER_DRAFT_GENERATION_FAILED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Failed",
            },
          },
        },
      };

    case "MATTER_DRAFT_CANCELLED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Cancelled",
            },
          },
        },
      };

    case "UPDATE_MATTER_DRAFT": {
      const { draftId, content, version, entityId, status, citations, justifications } = action.payload;
      const isComplete = Boolean(content && version);
      const existingCorrelationId = localStorage.getItem(`matter_draft:correlation:${entityId}:${draftId}`);
      const isDraftGenerating = !isComplete && Boolean(existingCorrelationId);

      const current = state.jobs?.[entityId]?.[draftId] ?? blankMatterDraftJob(draftId.toString(), existingCorrelationId || "");

      return {
        jobs: {
          ...state.jobs,
          [entityId]: {
            ...(state.jobs[entityId] || {}),
            [draftId]: {
              ...current,
              draftId,
              content,
              version,
              citations,
              justifications,
              finalStatus: status || current.finalStatus,
              isDraftGenerating,
              status: isComplete ? "Completed" : current.status,
            },
          },
        },
      };
    }

    case "UPDATE_MATTER_DRAFT_STATUS":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [draftId]: {
              ...baseJob,
              finalStatus: action.payload.status,
            },
          },
        },
      };

    default:
      return state;
  }
};